// Central purchase hook. ALL real payments go through Google Play Billing
// inside the Android TWA. When the user is NOT in the TWA (e.g. browsing on
// Chrome desktop), purchases are blocked with a clear message asking them to
// install the Android app — Stripe is intentionally disabled.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  isTwaWithPlayBilling,
  purchaseViaPlayBilling,
  reconcileActivePlayPurchases,
} from '../services/playBilling';

export function usePurchase() {
  const { token, refreshUser, isGuest } = useAuth();
  const [paymentProvider, setPaymentProvider] = useState('none');
  const [providerReady, setProviderReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const twa = await isTwaWithPlayBilling();
      if (!mounted) return;
      setPaymentProvider(twa ? 'play_billing' : 'none');
      setProviderReady(true);
      // Auto-reconcile Play purchases made offline / on another device
      if (twa && token && !isGuest) {
        const res = await reconcileActivePlayPurchases(token);
        if (res.reconciled > 0) await refreshUser();
      }
    })();
    return () => { mounted = false; };
  }, [token, isGuest, refreshUser]);

  // itemType: 'character' | 'level' | 'photos' | 'subscription'
  // itemId:   e.g. 'mira', 'sofia:3', 'sofia:3,4,5', 'luna', 'premium_monthly'
  // onPopupClosed (optional): fired the moment the user confirms in the Play
  //   popup, before the backend verification. Lets the UI swap to "Activation
  //   in progress" without waiting for the verify call to complete.
  const startPurchase = useCallback(async ({ itemType, itemId, onPopupClosed }) => {
    if (paymentProvider === 'play_billing') {
      const res = await purchaseViaPlayBilling({ itemType, itemId, token, onPopupClosed });
      if (res.success && !isGuest) await refreshUser();
      return { ...res, provider: 'play_billing' };
    }

    // Web fallback: Stripe is intentionally disabled. Tell the user to install
    // the Android app where Google Play Billing is available.
    return {
      success: false,
      provider: 'none',
      message:
        "Les achats sont uniquement disponibles depuis l'application Android. Installe MyCompanion AI depuis le Google Play Store pour débloquer ce contenu.",
    };
  }, [paymentProvider, token, isGuest, refreshUser]);

  return { startPurchase, paymentProvider, providerReady };
}
